/**
 * @typedef {Object} ConciliationRelDecisionDto
 * @property {boolean=} isCompleted
 * @property {string=} vendorCardCode
 * @property {string=} accountCode
 * @property {string=} taxCode
 * @property {Record<number,string|null>=} costingCodes
 * @property {string=} costingCode
 * @property {string=} costingCode2
 * @property {Record<number,string|null>=} suggestedCostingCodes
 * @property {string=} docCurrency
 * @property {string=} notes
 */

/**
 * @typedef {Object} ConciliationRelRowDto
 * @property {string} conciliationKey
 * @property {string=} estadoConcilia
 * @property {number|string=} prioridadConcilia
 * @property {string=} pfNombreProveedor
 * @property {string=} pfNitProveedor
 * @property {string=} goNombreProveedor
 * @property {string=} goNitProveedor
 * @property {boolean=} hasDecision
 * @property {boolean=} hasCompletedDecision
 * @property {boolean=} isProcessedPurchaseInvoice
 * @property {boolean=} isProcessedVendorPayment
 * @property {boolean=} isProcessedBoth
 * @property {boolean=} isIncorrectMatch
 * @property {ConciliationRelDecisionDto=} decision
 */

/**
 * @typedef {Object} ConciliationRelDetailDto
 * @property {string} conciliationKey
 * @property {string=} estadoConcilia
 * @property {number|string=} prioridadConcilia
 * @property {{dimensionCode:number,name?:string,description?:string,displayName?:string,isActive?:boolean}[]=} costingDimensions
 * @property {ConciliationRelDecisionDto=} decision
 * @property {boolean=} hasDecision
 * @property {boolean=} hasCompletedDecision
 * @property {boolean=} isProcessedPurchaseInvoice
 * @property {boolean=} isProcessedVendorPayment
 * @property {boolean=} isProcessedBoth
 * @property {boolean=} isIncorrectMatch
 */

/**
 * @typedef {Object} ConciliationRelWorkbenchResponseDto
 * @property {ConciliationRelDetailDto} row Complete, current REL record data.
 * @property {string|null} pdfUrl Short-lived, read-only invoice PDF URL.
 * @property {ConciliationRelDecisionDto|null} decision
 * @property {Object[]} actions
 * @property {{dimensionCode:number,name?:string,description?:string,displayName?:string,isActive?:boolean}[]} costingDimensions
 */

/**
 * @typedef {Object} ProcessRelResultDto
 * @property {string=} status
 * @property {number|string=} createdSapDocEntry
 * @property {number|string=} createdSapVendorPaymentDocEntry
 * @property {string[]=} messages
 */

const firstValue = (obj, keys = []) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

const normalizeBoolean = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

/**
 * @param {any} row
 * @returns {ConciliationRelRowDto}
 */
export const mapConciliationRelRow = (row = {}) => ({
  ...row,
  conciliationKey: firstValue(row, ['conciliationKey', 'ConciliationKey']),
  estadoConcilia: firstValue(row, ['estadoConcilia', 'EstadoConcilia']),
  prioridadConcilia: firstValue(row, ['prioridadConcilia', 'PrioridadConcilia']),
  pfNombreProveedor: firstValue(row, ['pfNombreProveedor', 'PFNombreProveedor']),
  pfNitProveedor: firstValue(row, ['pfNitProveedor', 'PFNitProveedor']),
  goNombreProveedor: firstValue(row, ['goNombreProveedor', 'GONombreProveedor']),
  goNitProveedor: firstValue(row, ['goNitProveedor', 'GONitProveedor']),
  hasDecision: normalizeBoolean(firstValue(row, ['hasDecision', 'HasDecision'])),
  hasCompletedDecision: normalizeBoolean(firstValue(row, ['hasCompletedDecision', 'HasCompletedDecision'])),
  isProcessedPurchaseInvoice: normalizeBoolean(firstValue(row, ['isProcessedPurchaseInvoice', 'IsProcessedPurchaseInvoice'])),
  isProcessedVendorPayment: normalizeBoolean(firstValue(row, ['isProcessedVendorPayment', 'IsProcessedVendorPayment'])),
  isProcessedBoth: normalizeBoolean(firstValue(row, ['isProcessedBoth', 'IsProcessedBoth'])),
  isIncorrectMatch: normalizeBoolean(firstValue(row, ['isIncorrectMatch', 'IsIncorrectMatch'])),
  decision: firstValue(row, ['decision', 'Decision'])
});

/**
 * @param {any} details
 * @returns {ConciliationRelDetailDto}
 */
export const mapConciliationRelDetail = (details = {}) => ({
  ...details,
  conciliationKey: firstValue(details, ['conciliationKey', 'ConciliationKey']),
  estadoConcilia: firstValue(details, ['estadoConcilia', 'EstadoConcilia']),
  prioridadConcilia: firstValue(details, ['prioridadConcilia', 'PrioridadConcilia']),
  costingDimensions: firstValue(details, ['costingDimensions', 'CostingDimensions']) ?? [],
  decision: firstValue(details, ['decision', 'Decision']) ?? null,
  hasDecision: normalizeBoolean(firstValue(details, ['hasDecision', 'HasDecision'])),
  hasCompletedDecision: normalizeBoolean(firstValue(details, ['hasCompletedDecision', 'HasCompletedDecision'])),
  isProcessedPurchaseInvoice: normalizeBoolean(firstValue(details, ['isProcessedPurchaseInvoice', 'IsProcessedPurchaseInvoice'])),
  isProcessedVendorPayment: normalizeBoolean(firstValue(details, ['isProcessedVendorPayment', 'IsProcessedVendorPayment'])),
  isProcessedBoth: normalizeBoolean(firstValue(details, ['isProcessedBoth', 'IsProcessedBoth'])),
  isIncorrectMatch: normalizeBoolean(firstValue(details, ['isIncorrectMatch', 'IsIncorrectMatch']))
});

/**
 * @param {any} result
 * @returns {ProcessRelResultDto}
 */
export const mapProcessRelResult = (result = {}) => ({
  ...result,
  status: firstValue(result, ['status', 'Status']),
  createdSapDocEntry: firstValue(result, ['createdSapDocEntry', 'CreatedSapDocEntry']),
  createdSapVendorPaymentDocEntry: firstValue(result, ['createdSapVendorPaymentDocEntry', 'CreatedSapVendorPaymentDocEntry']),
  messages: firstValue(result, ['messages', 'Messages']) ?? []
});

export const pickFirst = firstValue;
export const toBool = normalizeBoolean;
